import { useAuthStore } from '@/state/authStore';
import { useGameStore, durationSeconds } from '@/state/gameStore';
import { leaderboardService } from '@/services/leaderboardService';
import { userProfileService } from '@/services/userProfileService';
import { audioService } from '@/services/audioService';
import { shareGame } from '@/services/shareService';
import { clearRoot, el, mount } from './dom';
import { showMenu } from './MenuOverlay';
import { showRecords } from './RecordsOverlay';

export async function showGameOver(): Promise<void> {
  audioService.play('gameover');
  clearRoot();

  const game = useGameStore.getState();
  const auth = useAuthStore.getState();
  const seconds = durationSeconds(useGameStore.getState());

  const wasNewRecord = game.score > (auth.bestScore ?? 0);

  if (auth.uid) {
    userProfileService
      .recordGame(auth.uid, auth.displayName, auth.isAnonymous, game.score, game.level)
      .then(({ profile }) => {
        useAuthStore.getState().setProfile({
          nickname: profile.nickname,
          bestScore: profile.bestScore,
          bestLevel: profile.bestLevel,
          totalGames: profile.totalGames
        });
      })
      .catch((err) => console.error('recordGame failed', err));
  }
  if (game.score > 0) {
    useAuthStore.getState().setBestScore(game.score, game.level);
  }

  const container = el(
    'div',
    { class: 'fixed inset-0 flex flex-col items-center justify-center px-6 fade-in' },
    []
  );

  const panel = el(
    'div',
    { class: 'panel w-full max-w-sm p-6 flex flex-col items-center gap-3' },
    [
      el('div', { class: 'text-bad font-display text-sm tracking-[0.4em] uppercase' }, [
        'Fin del juego'
      ]),
      wasNewRecord
        ? el(
            'div',
            {
              class:
                'font-display text-2xl text-warn drop-shadow-[0_2px_12px_rgba(251,191,36,0.5)] mt-1'
            },
            ['🎉 ¡NUEVO RÉCORD!']
          )
        : empty(),
      el('div', { class: 'font-display text-6xl text-white' }, [
        game.score.toLocaleString('es-MX')
      ]),
      el('div', { class: 'text-white/60 text-sm' }, [
        `Nivel ${game.level + 1} · ${seconds}s`
      ]),
      !wasNewRecord && auth.bestScore > 0
        ? el('div', { class: 'text-white/40 text-xs' }, [
            `Tu récord: ${auth.bestScore.toLocaleString('es-MX')}`
          ])
        : empty()
    ]
  );

  const submitStatus = el('div', { class: 'text-white/50 text-xs h-4 text-center' }, ['']);

  const nicknameInput = el('input', {
    type: 'text',
    placeholder: 'Tu nickname',
    maxlength: 20,
    value: auth.displayName ?? '',
    class:
      'w-full rounded-xl bg-panel border border-white/10 text-white px-4 py-3 mt-2 focus:outline-none focus:border-accent'
  }) as HTMLInputElement;

  const submitBtn = el('button', { class: 'btn-primary w-full mt-2' }, ['📡 Subir récord']);
  submitBtn.addEventListener('click', async () => {
    const name = nicknameInput.value.trim();
    if (!name) {
      submitStatus.innerHTML = 'Escribe tu nickname.';
      return;
    }
    useAuthStore.getState().setNickname(name);
    submitBtn.setAttribute('disabled', 'true');
    submitBtn.innerHTML = '...';
    submitStatus.innerHTML = 'Subiendo...';

    if (auth.uid) {
      await userProfileService.setNickname(auth.uid, name, auth.isAnonymous);
    }

    const id = await leaderboardService.submit({
      userId: auth.uid ?? `local-${Date.now()}`,
      displayName: name,
      score: game.score,
      level: game.level,
      durationSeconds: seconds,
      isAnonymous: auth.isAnonymous
    });

    if (id) {
      submitStatus.innerHTML = '✓ Récord publicado';
      setTimeout(() => showRecords(), 800);
    } else {
      submitStatus.innerHTML =
        '⚠ No se pudo subir a la nube (¿reglas de Firestore?). Récord guardado localmente.';
      submitBtn.removeAttribute('disabled');
      submitBtn.innerHTML = '📡 Reintentar';
    }
  });

  const shareStatus = el('div', { class: 'text-white/50 text-xs h-4 text-center' }, ['']);
  const shareBtn = el('button', { class: 'btn-secondary w-full' }, ['📤 Compartir mi récord']);
  shareBtn.addEventListener('click', async () => {
    const nick = (nicknameInput.value.trim() || auth.displayName || '').slice(0, 20);
    const intro = nick ? `${nick} hizo` : 'Hice';
    const text = `🎯 ${intro} ${game.score.toLocaleString('es-MX')} puntos en Math Ninja (Nivel ${game.level + 1}). ¿Le ganas?`;
    shareBtn.setAttribute('disabled', 'true');
    const result = await shareGame(text);
    shareBtn.removeAttribute('disabled');
    if (result === 'copied') {
      shareStatus.innerHTML = '🔗 Link copiado al portapapeles';
    } else if (result === 'failed') {
      shareStatus.innerHTML = '⚠ No se pudo compartir';
    } else {
      shareStatus.innerHTML = '';
    }
  });

  const buttons = el('div', { class: 'flex flex-col gap-2 w-full max-w-sm mt-4' }, [
    nicknameInput,
    submitBtn,
    submitStatus,
    shareBtn,
    shareStatus,
    actionButton('🔁 Jugar otra vez', 'btn-secondary', () => {
      useGameStore.getState().startGame();
    }),
    actionButton('🏠 Menú', 'btn-ghost', () => {
      useGameStore.getState().reset();
      showMenu();
    })
  ]);

  container.append(panel, buttons);
  mount(container);
}

function empty(): HTMLElement {
  return el('div', { class: 'h-0' }, []);
}

function actionButton(label: string, cls: string, onClick: () => void): HTMLElement {
  const b = el('button', { class: `${cls} w-full` }, [label]);
  b.addEventListener('click', onClick);
  return b;
}
